import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, defaultIfEmpty, map, of } from 'rxjs';
import { ExtranetService } from '../services/extranet.service';
import { startSignIn } from '../auth/start-sign-in';
import { isAuthBypassed } from '../auth/auth-bypass';

// Hotel-side surfaces only (the Properties pages, from F2 on): an agency owns no property — it
// sells inventory that belongs to the hotels it contracts with, whose owners maintain the content
// in their own extranet (handoff R9) — so an AGENCY_MANAGER reaching such a route by URL is bounced
// Home rather than to /unauthorized, the same courtesy agencyManagerGuard extends the other way.
// A pure platform admin lands on their own admin home, as hotelManagerGuard already does.
//
// Deliberately a SEPARATE guard, not a narrowed hotelManagerGuard: that guard keeps admitting both
// manager roles on the shared routes (dashboard, calendar, bookings, settings, team/invite), so
// every existing route behaves exactly as before. Exported here, applied by F2 (WI #3294 only
// ships it with its spec).
//
// Same resolution path as the other guards: GET /api/extranet/access/me, never a B2C token claim.
export const hotelOnlyGuard: CanActivateFn = (_route, state) => {
  const msal = inject(MsalService);
  const router = inject(Router);
  const extranetSvc = inject(ExtranetService);

  // Locally there is no MSAL account to find — the interceptor authenticates with an unsigned dev
  // JWT instead — so only the sign-in step is skipped. Role resolution below still runs against the
  // real server, exactly as in production.
  if (!isAuthBypassed()) {
    const account = msal.instance.getActiveAccount();
    // Not signed in: B2C sign-in, then back to this very page (WI 3861) — see start-sign-in.ts.
    if (!account) return startSignIn(msal, router, state.url);
  }

  return extranetSvc.getMe().pipe(
    map((me) => {
      if (me.role === 'HOTEL_MANAGER') return true;
      router.navigate([homeFor(me.role)]);
      return false;
    }),
    catchError(() => {
      router.navigate(['/unauthorized']);
      return of(false);
    }),
    // GET /me completes without a value while the interceptor re-authenticates an expired
    // session by redirect (WI 3861): cancel this navigation, the redirect takes the page over.
    defaultIfEmpty(false),
  );
};

function homeFor(role: string): string {
  switch (role) {
    case 'AGENCY_MANAGER': return '/extranet/dashboard';
    case 'ADMIN': return '/extranet/admin';
    default: return '/unauthorized';
  }
}
